// ── CombatOverlay.jsx ────────────────────────────────────────────────────────
// Renders the tactical-preset selector and active QTEs on top of the canvas.
// QTE state lives inside the shared game-state object `st` — the overlay
// subscribes via a render-tick ref that the canvas loop advances each frame.
// On QTE success/failure, the overlay calls the provided resolveQte handler
// which mutates st.qtes and applies consequences.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";

export const TACTICAL_PRESETS = {
  offensive: {
    id: "offensive", label: "OFFENSIVE", color: "#ff5544", glow: "#ff4422",
    buffs:   { blasterDamage: 1.5, blasterBulletSpeed: 1.2 },
    debuffs: { shieldMax: 0.75, shipSpeed: 0.9 },
    desc: "+50% Blaster · +20% Bullet Speed · −25% Shield · −10% Speed",
  },
  defensive: {
    id: "defensive", label: "DEFENSIVE", color: "#4488ff", glow: "#3366dd",
    buffs:   { shieldRecharge: 1.5, dodgeWindow: 1.15 },
    debuffs: { blasterDamage: 0.75, noCrits: true },
    desc: "+50% Shield Recharge · +15% Dodge · −25% Blaster · No Crits",
  },
  balanced: {
    id: "balanced", label: "BALANCED", color: "#ffdd66", glow: "#ccbb33",
    buffs: {}, debuffs: {},
    desc: "Standard stats (100%)",
  },
};

// Slider QTE — moving indicator; tap when overlapping the green band
function SliderQte({ q, onResolve }) {
  const pct = q.indicator * 100;
  const greenLeft  = q.greenStart * 100;
  const greenWidth = (q.greenEnd - q.greenStart) * 100;
  return (
    <div style={{
      background: "rgba(8,6,24,0.95)", border: "1px solid rgba(255,180,40,0.4)",
      borderRadius: 10, padding: "10px 12px", minWidth: 260,
      boxShadow: "0 0 30px rgba(255,180,40,0.3)",
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#ffbb44", marginBottom: 6 }}>
        {q.label} · {Math.ceil(q.timeLeft / 1000)}s
      </div>
      <div style={{ position: "relative", height: 22, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" }}>
        <div style={{
          position: "absolute", left: `${greenLeft}%`, top: 0, bottom: 0,
          width: `${greenWidth}%`, background: "rgba(68,255,153,0.35)",
          borderLeft: "2px solid #44ff99", borderRight: "2px solid #44ff99",
        }} />
        <div style={{
          position: "absolute", left: `${pct}%`, top: -2, bottom: -2, width: 3,
          background: "#ffeecc", boxShadow: "0 0 8px #ffaa44", transform: "translateX(-50%)",
        }} />
      </div>
      <button onClick={() => {
        const inGreen = q.indicator >= q.greenStart && q.indicator <= q.greenEnd;
        onResolve(q.id, inGreen);
      }} style={{
        marginTop: 8, width: "100%", padding: "8px 0",
        background: "linear-gradient(180deg,#ff7744,#cc4422)",
        border: "1px solid #ffaa66", borderRadius: 6, color: "#fff",
        fontWeight: 800, letterSpacing: 1, fontSize: 12, cursor: "pointer",
      }}>BREAK TETHER</button>
    </div>
  );
}

// Sequence QTE — four numbered buttons; tap in 1→4 order
function SequenceQte({ q, onResolve }) {
  // q.slots is a shuffled 4-tuple. q.next = next correct number (1..4)
  return (
    <div style={{
      background: "rgba(8,6,24,0.95)", border: "1px solid rgba(255,80,200,0.5)",
      borderRadius: 10, padding: "10px 12px",
      boxShadow: "0 0 30px rgba(255,80,200,0.3)",
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#ff88cc", marginBottom: 6 }}>
        {q.label} · Next: {q.next} · {Math.ceil(q.timeLeft / 1000)}s
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6 }}>
        {q.slots.map((n, i) => (
          <button key={i} disabled={n < q.next} onClick={() => {
            if (n === q.next) {
              if (n === 4) onResolve(q.id, true);
              else q.next = n + 1;
            } else {
              onResolve(q.id, false);
            }
          }} style={{
            padding: "14px 0",
            background: n < q.next ? "rgba(255,255,255,0.08)"
              : n === q.next ? "linear-gradient(180deg,#ff88cc,#cc3399)"
              : "rgba(255,120,220,0.25)",
            border: `1px solid ${n === q.next ? "#ffaadd" : "rgba(255,120,220,0.4)"}`,
            borderRadius: 6,
            color: n < q.next ? "rgba(255,255,255,0.3)" : "#fff",
            fontWeight: 800, fontSize: 18, cursor: n < q.next ? "default" : "pointer",
            opacity: n < q.next ? 0.5 : 1,
          }}>{n}</button>
        ))}
      </div>
    </div>
  );
}

// Breach QTE — rotating ring with a lit arc. Tap when the indicator is inside.
function BreachQte({ q, onResolve }) {
  const R = 54, cx = 70, cy = 70;
  // Indicator at top of ring; the lit arc rotates around q.lit
  // success = indicator angle (which is -π/2 in ring space, i.e. top) minus q.ang rotation
  // equivalent: the arc [q.lit, q.lit + litArc] must include (0 - q.ang) normalized
  const norm = (a) => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  const indicatorAngle = norm(-Math.PI / 2 - q.ang);
  const litStart = norm(q.lit);
  const litEnd   = norm(q.lit + q.litArc);

  function toXY(ang, r) { return [cx + Math.cos(ang) * r, cy + Math.sin(ang) * r]; }

  return (
    <div style={{
      background: "rgba(8,6,24,0.95)", border: "1px solid rgba(255,220,80,0.5)",
      borderRadius: 10, padding: "10px 12px", textAlign: "center",
      boxShadow: "0 0 30px rgba(255,220,80,0.35)",
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "#ffdd66", marginBottom: 6 }}>
        {q.label} · {Math.ceil(q.timeLeft / 1000)}s
      </div>
      <svg width={140} height={140} style={{ display: "block", margin: "0 auto" }}>
        <circle cx={cx} cy={cy} r={R} stroke="rgba(255,255,255,0.15)" strokeWidth="6" fill="none" />
        <path d={`M ${toXY(q.lit, R).join(" ")} A ${R} ${R} 0 ${q.litArc > Math.PI ? 1 : 0} 1 ${toXY(q.lit + q.litArc, R).join(" ")}`}
          stroke="#ffdd44" strokeWidth="7" fill="none" strokeLinecap="round" />
        <circle cx={toXY(indicatorAngle, R)[0]} cy={toXY(indicatorAngle, R)[1]} r="5" fill="#ffeebb" />
      </svg>
      <button onClick={() => {
        const inArc = litStart < litEnd
          ? (indicatorAngle >= litStart && indicatorAngle <= litEnd)
          : (indicatorAngle >= litStart || indicatorAngle <= litEnd);
        onResolve(q.id, inArc);
      }} style={{
        width: "100%", padding: "10px 0",
        background: "linear-gradient(180deg,#ffdd44,#cc9911)",
        border: "1px solid #ffee88", borderRadius: 6, color: "#281800",
        fontWeight: 800, letterSpacing: 1.5, fontSize: 13, cursor: "pointer",
      }}>BREACH</button>
    </div>
  );
}

export function CombatOverlay({
  stRef, activePreset, onPresetChange,
  onResolveQte, coreHeatRef, coreHeatMax, isEnemyNode, streakRef,
}) {
  // Force re-render each animation frame so QTE timers/indicators update
  const [, setTick] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    const tick = () => { setTick(x => (x + 1) & 0xffff); rafRef.current = requestAnimationFrame(tick); };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const qtes = stRef.current?.qtes ?? [];
  const heat = coreHeatRef.current ?? 0;
  const heatPct = Math.min(1, heat / coreHeatMax);
  const heatLocked = stRef.current?.coreHeatLocked;

  return (
    <>

      {/* Core Heat bar — appears in combat */}
      {isEnemyNode && (
        <div style={{
          position: "absolute", top: 32, left: 6, zIndex: 4,
          width: 110, pointerEvents: "none",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: heatLocked ? "#ff4444" : "#ffaa44", letterSpacing: 0.5 }}>
              {heatLocked ? "⚠ OVERHEAT" : "CORE HEAT"}
            </span>
            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.4)" }}>{Math.round(heatPct * 100)}%</span>
          </div>
          <div style={{ height: 5, background: "rgba(255,255,255,0.08)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: `${heatPct * 100}%`,
              background: heatLocked ? "#ff4444" : heatPct > 0.7 ? "#ff8822" : "#ffbb44",
              transition: "width 0.15s linear",
              boxShadow: `0 0 6px ${heatLocked ? "#ff4444aa" : "#ff882288"}`,
            }} />
          </div>
        </div>
      )}

      {/* Perfect streak badge */}
      {isEnemyNode && streakRef.current > 0 && (
        <div style={{
          position: "absolute", top: 58, left: 6, zIndex: 4,
          background: "rgba(100,50,160,0.85)", border: "1px solid #aa77ff",
          borderRadius: 4, padding: "2px 6px", fontSize: 10, fontWeight: 800,
          color: "#ffccff", letterSpacing: 0.5, pointerEvents: "none",
        }}>★ STREAK ×{streakRef.current}</div>
      )}

      {/* Active QTEs (center-bottom stack) */}
      {qtes.length > 0 && (
        <div style={{
          position: "absolute", bottom: 40, left: "50%", transform: "translateX(-50%)",
          zIndex: 6, display: "flex", flexDirection: "column", gap: 8, pointerEvents: "auto",
        }}>
          {qtes.map(q => {
            if (q.kind === "slider")   return <SliderQte   key={q.id} q={q} onResolve={onResolveQte} />;
            if (q.kind === "sequence") return <SequenceQte key={q.id} q={q} onResolve={onResolveQte} />;
            if (q.kind === "breach")   return <BreachQte   key={q.id} q={q} onResolve={onResolveQte} />;
            return null;
          })}
        </div>
      )}
    </>
  );
}
